<template>
    <hox-modal data-testid="multi-preview-share__modal" @close="close">
        <template #header>Share Preview Links</template>

        <div class="multi-preview-share__content">
            <template v-if="phase === PHASE.ONE">
                <div class="multi-preview-share__section">
                    <h3 class="multi-preview-share__title">{{ deliverables.length }} Deliverables</h3>

                    <ul class="multi-preview-share__deliverable-list">
                        <li
                            v-for="deliverable of deliverables"
                            :key="deliverable.idHash"
                            class="multi-preview-share__deliverable-label"
                        >
                            {{ deliverable.reportingLabel }}
                        </li>
                    </ul>
                </div>
            </template>

            <template v-if="phase === PHASE.TWO">
                <div class="multi-preview-share__section">
                    <Alert banner type="warning">
                        Shareable links are temporary
                        <template #desc>
                            They are only valid for a limited time, after which they will no longer work.
                        </template>
                    </Alert>
                </div>

                <div class="multi-preview-share__section">
                    <h3 class="multi-preview-share__title">Your shareable link is ready</h3>

                    <div class="multi-preview-share__url-row">
                        <Input class="multi-preview-share__preview-url-input" :model-value="shareUrl" readonly />
                        <Button ghost type="primary" @click="copyTextToClipboard()">
                            {{ copiedToClipboard ? "Copied ✓" : "Copy" }}
                        </Button>
                    </div>
                </div>

                <GrayPanel class-name="multi-preview-share__section">
                    <h3 class="multi-preview-share__title">Recipient Email Addresses</h3>

                    <MultiInput
                        v-model="emailAddresses"
                        :error="validationErrors.emailAddresses"
                        :errors-by-index="validationErrors.emailAddressesByIndex"
                        :show-errors="showValidationErrors"
                        new-value-placeholder="Type here to add an email address"
                        data-testid="share-links__modal-email"
                        @inputRemoved="blurredInputs.splice($event as number, 1)"
                        @inputBlur="blurredInputs[$event as number] = true"
                    />
                </GrayPanel>
            </template>

            <Alert v-if="isError" banner type="error">
                <p>There was an error while trying to get the shareable link</p>
                <template #desc>
                    Hopefully it was a temporary issue and should work if you try again in a few moments.
                </template>
            </Alert>
        </div>

        <template #footer>
            <Button data-testid="share-links__modal-cancel-button" @click="close">Cancel</Button>

            <Button
                v-if="phase === PHASE.ONE"
                :loading="isLoading"
                :disabled="isLoading"
                type="primary"
                data-testid="share-links__modal-generate-button"
                @click="getMultipleShareUrl"
            >
                Generate shareable link
            </Button>

            <Button
                v-if="phase === PHASE.TWO"
                :disabled="hasValidationErrors && showValidationErrors"
                :loading="isSendingPreviewUrls"
                type="primary"
                data-testid="share-links__modal-send-button"
                @click="sendPreviewLinks"
            >
                Send Preview Link
            </Button>
        </template>
    </hox-modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";

import validateEmail from "hoxton-shared/utils/validateEmail";

import type { ConstructedDeliverable } from "@/types/types";
import { apolloClient } from "@/apollo";
import GetMultipleShareUrl from "@/apollo/mutations/v2/GetMultipleShareUrl.gql";
import SendPreviewLinks from "@/apollo/mutations/v2/SendPreviewLinks.gql";
import getComputedDeliverableIdentifiers from "@/services/deliverables/getComputedDeliverableIdentifiers";
import { snackbar } from "@/utils";
import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";

import MultiInput from "@/components/common/MultiInput.vue";
import GrayPanel from "@/components/Card/GrayPanel.vue";

const emit = defineEmits<{ close: [] }>();
const { campaignId, deliverables } = defineProps<{ campaignId: string; deliverables: ConstructedDeliverable[] }>();

const shareUrl = ref<string | null>(null);
const copiedToClipboard = ref(false);
const isLoading = ref(false);
const isError = ref(false);
const isCompleteEventTracked = ref(false);

const PHASE = {
    ONE: "ONE",
    TWO: "TWO"
};

const phase = computed(() => {
    if (shareUrl.value) {
        return PHASE.TWO;
    }

    return PHASE.ONE;
});

const ga4 = useGA4Tracking();

onMounted(() => {
    ga4.trackFeatureEvent({
        featureEvent: GA4.EVENT_TYPES.FEATURE_ACTIVATE,
        featureCategory: GA4.FEATURE_CATEGORIES.SHARE,
        featureContext: GA4.FEATURE_CONTEXTS.SHARE_PREVIEW_LINKS
    });
});

function close() {
    if (!isCompleteEventTracked.value) {
        ga4.trackFeatureEvent({
            featureEvent: GA4.EVENT_TYPES.FEATURE_COMPLETE,
            featureCategory: GA4.FEATURE_CATEGORIES.SHARE,
            featureContext: GA4.FEATURE_CONTEXTS.CANCEL_SHARE_PREVIEW_LINKS
        });
    }

    emit("close");
}

async function getMultipleShareUrl() {
    if (!apolloClient) {
        throw new Error("Apollo client not initialized");
    }

    const computedDeliverableIdentifiers = getComputedDeliverableIdentifiers(deliverables);
    const reportingLabels = deliverables.map(deliverable => deliverable.reportingLabel);

    try {
        isError.value = false;
        isLoading.value = true;
        const res = await apolloClient.mutate<{ getMultipleShareUrl: { url: string } }>({
            mutation: GetMultipleShareUrl,
            variables: {
                campaignId: campaignId,
                computedDeliverableIdentifiers,
                reportingLabels
            }
        });

        if (!res.data) {
            throw new Error("No data returned from apollo client");
        }

        shareUrl.value = res.data.getMultipleShareUrl.url.replace("share", "preview");
    } catch (err) {
        console.error(err);
        isError.value = true;
    } finally {
        isLoading.value = false;
    }
}

async function copyTextToClipboard() {
    if (!shareUrl.value) {
        console.error("No share url to copy");
        return;
    }

    try {
        await navigator.clipboard.writeText(shareUrl.value);
        copiedToClipboard.value = true;
    } catch (err) {
        console.error(err);
    }
}

/** EMAILS */
const emailAddresses = ref<string[]>([]);
const blurredInputs = ref<boolean[]>([]);
const showValidationErrors = ref(false);
const isSendingPreviewUrls = ref(false);

const validationErrors = computed(() => {
    const errors = {
        emailAddresses: [] as string[],
        emailAddressesByIndex: {} as Record<number, string[]>
    };

    const toValidate = emailAddresses.value.filter((v, i) => !!blurredInputs.value[i]);

    if (toValidate.length === 0) {
        errors.emailAddresses = ["At least 1 email address must be provided in order to share preview links."];

        return errors;
    }

    toValidate.forEach((emailAddress, index) => {
        const isValid = validateEmail(emailAddress);

        if (!isValid) {
            errors.emailAddressesByIndex[index] = [
                `'${emailAddress}' doesn’t look like an email address. Please double-check it or try to include an “@”.`
            ];
        }
    });

    return errors;
});

const hasValidationErrors = computed(() => {
    return (
        validationErrors.value.emailAddresses.length > 0 ||
        Object.keys(validationErrors.value.emailAddressesByIndex).length > 0
    );
});

async function sendPreviewLinks() {
    if (!apolloClient) {
        throw new Error("Apollo client not initialized");
    }

    if (!shareUrl.value) {
        throw new Error("No share url");
    }

    try {
        isError.value = false;
        showValidationErrors.value = true;

        if (hasValidationErrors.value) {
            return;
        }

        isSendingPreviewUrls.value = true;

        const res = await apolloClient.mutate<{ sendPreviewLinks: { ok: boolean } }>({
            mutation: SendPreviewLinks,
            variables: {
                campaignId: campaignId,
                emailAddresses: emailAddresses.value,
                previewUrl: shareUrl.value
            }
        });

        if (!res.data) {
            throw new Error("No data returned from apollo client");
        }

        if (!res.data.sendPreviewLinks.ok) {
            throw new Error("Failed to send preview links");
        }

        close();

        snackbar.success("Preview link shared");

        ga4.trackFeatureEvent({
            featureEvent: GA4.EVENT_TYPES.FEATURE_COMPLETE,
            featureCategory: GA4.FEATURE_CATEGORIES.SHARE,
            featureContext: GA4.FEATURE_CONTEXTS.SEND_PREVIEW_LINKS
        });

        isCompleteEventTracked.value = true;
    } catch (err) {
        console.error(err);
        isError.value = true;
    } finally {
        isSendingPreviewUrls.value = false;
    }
}
</script>

<style lang="css">
.multi-preview-share__content {
    display: flex;
    flex-direction: column;
    gap: 32px;
}

.multi-preview-share__section {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.multi-preview-share__title-row {
    display: flex;
    justify-content: space-between;
    align-items: center;

    height: 48px;
}

.multi-preview-share__deliverable-list {
    display: flex;
    flex-direction: column;
    gap: 8px;

    max-height: 65vh;
    overflow-y: auto;
}

.multi-preview-share__deliverable-label {
    flex-grow: 1;
    flex-shrink: 0;

    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
}

.multi-preview-share__url-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
}

.multi-preview-share__preview-url-input {
    flex-grow: 1;
}
</style>
